import type { VariantProps } from 'cva'
import * as React from 'react'

import { cn, cva } from '~/utils/classnames'

// Context for coordinating toggle group items
interface ToggleGroupContextValue<T> {
  value: T
  onChange: (value: T) => void
  name: string
  compare: (a: T, b: T) => boolean
}

const ToggleGroupContext =
  React.createContext<ToggleGroupContextValue<unknown> | null>(null)

function useToggleGroupContext<T>() {
  const context = React.use(
    ToggleGroupContext
  ) as ToggleGroupContextValue<T> | null
  if (!context) {
    throw new Error('ToggleGroup.Item must be used within ToggleGroup')
  }
  return context
}

// Root component - minimal wrapper, browser handles navigation
interface ToggleGroupProps<T> {
  value: T
  onValueChange: (value: T) => void
  name: string
  children: React.ReactNode
  className?: string
  disabled?: boolean
  compare?: (a: T, b: T) => boolean
}

function ToggleGroupRoot<T>({
  value,
  onValueChange,
  name,
  children,
  className,
  disabled = false,
  compare = (a, b) => a === b,
}: ToggleGroupProps<T>) {
  const contextValue = React.useMemo(
    () => ({
      value,
      onChange: onValueChange,
      name,
      compare,
    }),
    [value, onValueChange, name, compare]
  )

  return (
    <ToggleGroupContext
      value={contextValue as ToggleGroupContextValue<unknown>}
    >
      <div
        role="radiogroup"
        className={cn('flex flex-wrap items-center gap-2', className)}
        data-disabled={disabled || undefined}
      >
        {children}
      </div>
    </ToggleGroupContext>
  )
}

// Toggle group item styles - styled label that wraps native input
const toggleGroupItemStyle = cva({
  base: [
    'relative whitespace-nowrap inline-flex items-center justify-center gap-1.5 font-medium transition cursor-pointer',
    'border border-border/8 bg-transparent',
    'text-foreground',
    'has-[:focus-visible]:outline-none has-[:focus-visible]:ring-4 has-[:focus-visible]:ring-accent-element/50 has-[:focus-visible]:border-accent-element',
    'has-[:disabled]:opacity-40 has-[:disabled]:pointer-events-none',
  ],
  variants: {
    size: {
      xs: 'rounded-sm px-1.5 text-xs h-5',
      sm: 'rounded-md px-3 text-xs h-8',
      md: 'rounded-md px-4 text-sm h-9',
      lg: 'rounded-lg px-5 text-base h-12',
    },
    checked: {
      true: ['border-accent-element', 'ring-2 ring-accent-element/50'],
      false: ['bg-transparent hover:bg-under/4 active:bg-under/6'],
    },
  },
  defaultVariants: {
    size: 'md',
    checked: false,
  },
})

interface ToggleGroupItemProps<T>
  extends
    Omit<
      React.ComponentPropsWithRef<'input'>,
      'value' | 'onChange' | 'type' | 'size'
    >,
    VariantProps<typeof toggleGroupItemStyle> {
  value: T
  children: React.ReactNode
}

const ToggleGroupItem = <T,>({
  children,
  className,
  value: itemValue,
  size = 'md',
  disabled,
  ...props
}: ToggleGroupItemProps<T>) => {
  const {
    value: groupValue,
    onChange,
    name,
    compare,
  } = useToggleGroupContext<T>()
  const checked = compare(groupValue, itemValue)

  return (
    <label
      className={cn(
        toggleGroupItemStyle({
          className,
          size,
          checked,
        })
      )}
      data-state={checked ? 'checked' : 'unchecked'}
    >
      <input
        type="radio"
        name={name}
        value={String(itemValue)}
        checked={checked}
        disabled={disabled}
        onChange={() => onChange(itemValue)}
        className="sr-only"
        {...props}
      />
      {children}
    </label>
  )
}

const ToggleGroup = Object.assign(ToggleGroupRoot, {
  Item: ToggleGroupItem,
})

export { ToggleGroup, toggleGroupItemStyle }
export type { ToggleGroupItemProps, ToggleGroupProps }
